<div class="min-h-full flex flex-col p-6 lg:p-12 items-center relative">
  <div class="w-full max-w-6xl mx-auto flex flex-col items-center">
    <header class="mb-10 flex flex-col gap-3 text-center">
      <h1
        class="text-3xl lg:text-4xl font-extrabold text-heading tracking-tight"
      >
        {{ config.texts.pageTitle }}
      </h1>
      <p class="text-lg text-secondary-text max-w-2xl mx-auto">
        {{ config.texts.pageSubtitle }}
      </p>
    </header>

    @if (importSuccess()) {
      <div
        class="w-full max-w-2xl bg-component-inner border border-primary-border rounded-xl shadow-default p-10 text-center flex flex-col items-center"
      >
        <div
          class="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mb-6"
        >
          <i class="fas fa-check text-4xl text-success"></i>
        </div>
        <h2 class="text-2xl font-bold text-heading mb-2">
          {{ config.texts.successTitle }}
        </h2>
        <p class="text-secondary-text mb-8">
          {{ config.texts.successSubtitle }}
        </p>

        <div class="w-full flex flex-col gap-3 mb-8">
          @for (state of fileStates(); track state.id) {
            <div
              class="flex items-center justify-between p-4 bg-alt-background border border-secondary-border rounded-lg text-left"
            >
              <div class="flex items-center gap-3">
                <i class="fas fa-database text-primary text-xl"></i>
                <div class="flex flex-col">
                  <span class="font-bold text-primary-text">{{
                    state.schema?.modelName
                  }}</span>
                  <span class="text-xs text-secondary-text font-mono">{{
                    state.file.name
                  }}</span>
                </div>
              </div>
              <div
                class="text-sm text-secondary-text font-medium bg-component-inner px-3 py-1 rounded border border-primary-border"
              >
                {{ state.schema?.fields?.length }}
                {{ config.texts.summaryFieldsCount }}
              </div>
            </div>
          }
        </div>

        <button
          (click)="goToDashboard()"
          class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-8 py-3 transition-colors shadow-sm"
        >
          {{ config.texts.continueButton }}
        </button>
      </div>
    } @else {
      <div class="w-full flex flex-col gap-8">
        <nz-upload
          nzType="drag"
          [nzMultiple]="true"
          [nzBeforeUpload]="beforeUpload"
          [nzShowUploadList]="false"
          class="w-full block bg-component-inner border-2 border-dashed border-primary-border rounded-xl hover:border-primary hover:bg-hover-default transition-all"
        >
          <p class="ant-upload-drag-icon text-center mt-8">
            <i
              class="fad fa-cloud-upload-alt text-5xl text-primary opacity-80"
            ></i>
          </p>
          <p class="ant-upload-text text-lg font-bold text-heading mt-4">
            {{ config.texts.uploadTitle }}
          </p>
          <p class="ant-upload-hint text-secondary-text mb-8 max-w-md mx-auto">
            {{ config.texts.supportedFiles }}
          </p>
        </nz-upload>

        @if (processingFiles().length > 0) {
          <div class="flex flex-col gap-3">
            @for (state of processingFiles(); track state.id) {
              <div
                class="flex items-center justify-between p-4 bg-component-inner border border-primary-border rounded-lg shadow-sm"
              >
                <div class="flex items-center gap-4">
                  <div
                    class="w-10 h-10 bg-alt-background rounded flex items-center justify-center border border-secondary-border"
                  >
                    <i class="fas fa-file-csv text-primary-text text-lg"></i>
                  </div>
                  <div class="flex flex-col">
                    <span class="font-bold text-primary-text">{{
                      state.file.name
                    }}</span>
                    <span class="text-xs text-secondary-text"
                      >{{ (state.file.size / 1024).toFixed(1) }} KB</span
                    >
                  </div>
                </div>

                <div class="flex items-center gap-4">
                  @if (state.isAnalyzing) {
                    <div class="flex items-center gap-2 text-primary">
                      <i class="fas fa-circle-notch fa-spin text-xl"></i>
                      <span class="text-sm font-medium">Analyzing...</span>
                    </div>
                  }
                  @if (state.error) {
                    <span class="text-danger text-sm font-semibold"
                      ><i class="fas fa-exclamation-triangle mr-1"></i>
                      {{ state.error }}</span
                    >
                  }
                  <button
                    (click)="confirmRemoveFile(state.id)"
                    class="text-secondary-text hover:text-danger focus:outline-none transition-colors w-8 h-8 rounded hover:bg-alt-background flex items-center justify-center"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        }

        @if (readyFiles().length > 0) {
          <div
            class="sticky top-0 z-[9] bg-body-background w-[calc(100%+2px)] ml-[-1px] mr-[-1px] pt-[10px]"
          >
            <div
              class="bg-body-background z-0 pt-2 pb-4 mt-2 border-b border-primary-border mb-4 flex flex-col gap-4"
            >
              <div class="flex items-center justify-between">
                <h2 class="text-xl font-bold text-heading">
                  {{ config.texts.importingFilesTitle }}
                </h2>
                <button
                  (click)="toggleAllExpansions()"
                  class="text-sm font-semibold px-4 py-2 rounded-lg bg-component-inner border border-secondary-border text-secondary-text hover:text-primary hover:border-primary transition-colors flex items-center gap-2 shadow-sm"
                >
                  <i
                    class="fas"
                    [ngClass]="
                      areAllExpanded() ? 'fa-compress-alt' : 'fa-expand-alt'
                    "
                  ></i>
                  {{
                    areAllExpanded()
                      ? config.texts.collapseAllButton
                      : config.texts.expandAllButton
                  }}
                </button>
              </div>

              <div class="flex flex-wrap gap-2">
                @for (state of readyFiles(); track state.id) {
                  <button
                    (click)="scrollToCard(state.id)"
                    class="px-3 py-1.5 rounded-lg bg-component-inner border border-secondary-border text-sm text-primary-text hover:border-primary transition-colors text-left flex items-center gap-2 shadow-sm"
                  >
                    <i class="fas fa-file-csv text-secondary-text"></i>
                    <span
                      >{{ state.file.name }}
                      <span class="text-secondary-text"
                        >({{ state.schema?.modelName }})</span
                      ></span
                    >
                  </button>
                }
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-8">
            @for (state of readyFiles(); track state.id) {
              @if (state.schema; as schema) {
                <div
                  [id]="'card-' + state.id"
                  class="bg-component-inner border border-primary-border rounded-xl shadow-sm flex flex-col overflow-hidden"
                >
                  <div
                    class="bg-component-header border-b border-secondary-border p-6 flex flex-col gap-4 cursor-pointer hover:bg-hover-default transition-colors"
                    (click)="toggleFileExpansion(state.id)"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <i class="fad fa-database text-primary text-2xl"></i>
                        <span class="font-mono text-sm text-secondary-text">{{
                          state.file.name
                        }}</span>
                      </div>
                      <div class="flex items-center gap-3">
                        <button
                          (click)="
                            $event.stopPropagation();
                            confirmReanalyzeFile(state.id)
                          "
                          class="text-xs font-semibold px-3 py-1.5 rounded bg-alt-background border border-secondary-border text-secondary-text hover:text-primary hover:border-primary transition-colors flex items-center"
                        >
                          <i class="fas fa-sync-alt mr-1.5"></i>
                          {{ config.texts.reanalyzeButton }}
                        </button>
                        <button
                          (click)="
                            $event.stopPropagation();
                            confirmRemoveFile(state.id)
                          "
                          class="text-secondary-text hover:text-danger focus:outline-none transition-colors p-2 rounded hover:bg-alt-background flex items-center justify-center"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                        <button
                          class="text-secondary-text hover:text-primary focus:outline-none transition-colors ml-2"
                        >
                          <i
                            class="fas"
                            [ngClass]="
                              state.isExpanded
                                ? 'fa-chevron-up'
                                : 'fa-chevron-down'
                            "
                          ></i>
                        </button>
                      </div>
                    </div>

                    <div class="flex flex-col gap-1 mt-1">
                      <span class="font-bold text-heading text-lg">{{
                        schema.modelName
                      }}</span>
                      <span class="text-sm text-secondary-text">{{
                        schema.modelDescription
                      }}</span>
                    </div>
                  </div>

                  @if (!state.isExpanded) {
                    <button
                      class="p-8 text-center text-secondary-text bg-component-inner cursor-pointer hover:bg-hover-default transition-colors w-full"
                      (click)="toggleFileExpansion(state.id)"
                    >
                      <p class="font-medium">
                        <span><i class="fa-regular fa-eye-slash"></i></span>
                        &nbsp; {{ config.texts.collapsedMessage }}
                      </p>
                    </button>
                  } @else {
                    <div class="p-6 flex flex-col gap-8">
                      <div class="flex flex-col gap-4">
                        <h4
                          class="text-lg font-bold text-heading border-b border-secondary-border pb-2"
                        >
                          {{ config.texts.aiRecommendationsTitle }}
                        </h4>

                        <nz-table
                          #readOnlyTable
                          [nzData]="state.originalSchema?.fields || []"
                          nzSize="small"
                          [nzShowPagination]="false"
                          class="border border-secondary-border rounded"
                        >
                          <thead>
                            <tr class="bg-alt-background">
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colOriginal }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colField }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colTitle }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colType }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold text-center"
                              >
                                {{ config.texts.colRequired }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold text-center"
                              >
                                {{ config.texts.colSensitive }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colDesc }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (
                              field of readOnlyTable.data;
                              track field.originalColumnName
                            ) {
                              <tr
                                class="hover:bg-hover-default transition-colors"
                              >
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-secondary-text font-mono text-sm"
                                >
                                  {{ field.originalColumnName }}
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-primary-text text-sm font-mono"
                                >
                                  {{ field.suggestedFieldName }}
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-primary-text text-sm"
                                >
                                  {{ field.suggestedTitle }}
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border"
                                >
                                  <span
                                    class="bg-section-background border border-secondary-border text-secondary-text text-xs px-2 py-1 rounded"
                                    >{{
                                      getCombinedType(field.type, field.isArray)
                                    }}</span
                                  >
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-center"
                                >
                                  <i
                                    class="fas"
                                    [ngClass]="
                                      field.required
                                        ? 'fa-check text-success'
                                        : 'fa-times text-secondary-text'
                                    "
                                  ></i>
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-center"
                                >
                                  <i
                                    class="fas"
                                    [ngClass]="
                                      field.sensitive
                                        ? 'fa-check text-success'
                                        : 'fa-times text-secondary-text'
                                    "
                                  ></i>
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-secondary-text text-sm"
                                >
                                  {{ field.description }}
                                </td>
                              </tr>
                            }
                          </tbody>
                        </nz-table>
                      </div>

                      <div class="flex flex-col gap-4">
                        <h4
                          class="text-lg font-bold text-heading border-b border-secondary-border pb-2"
                        >
                          {{ config.texts.customizeRecommendationsTitle }}
                        </h4>

                        <div class="flex flex-col gap-4 mb-2">
                          <div class="flex flex-col gap-2 w-full">
                            <label
                              class="text-sm font-semibold text-primary-text"
                              >{{ config.texts.modelNameLabel }}
                              <span class="text-danger">*</span></label
                            >
                            <input
                              nz-input
                              [ngModel]="schema.modelName"
                              (ngModelChange)="
                                updateSchemaRootProperty(
                                  state.id,
                                  'modelName',
                                  $event
                                )
                              "
                              class="bg-input-background border-secondary-border focus:ring-focus-ring text-primary-text font-bold"
                            />
                          </div>
                          <div class="flex flex-col gap-2 w-full">
                            <label
                              class="text-sm font-semibold text-primary-text"
                              >{{ config.texts.modelDescLabel }}
                              <span class="text-danger">*</span></label
                            >
                            <textarea
                              nz-input
                              rows="3"
                              required
                              [ngModel]="schema.modelDescription"
                              (ngModelChange)="
                                updateSchemaRootProperty(
                                  state.id,
                                  'modelDescription',
                                  $event
                                )
                              "
                              class="bg-input-background border-secondary-border focus:ring-focus-ring text-primary-text resize-y custom-scrollbar"
                            ></textarea>
                          </div>
                        </div>

                        <nz-table
                          #basicTable
                          [nzData]="schema.fields"
                          nzSize="small"
                          [nzShowPagination]="false"
                          class="border border-secondary-border rounded"
                        >
                          <thead>
                            <tr class="bg-alt-background">
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colOriginal }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colField }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold"
                              >
                                {{ config.texts.colTitle }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold w-40"
                              >
                                {{ config.texts.colType }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold text-center w-24"
                              >
                                {{ config.texts.colRequired }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold text-center w-24"
                              >
                                {{ config.texts.colSensitive }}
                              </th>
                              <th
                                class="!bg-alt-background text-primary-text font-semibold text-center w-24"
                              >
                                {{ config.texts.colImport }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (
                              field of basicTable.data;
                              track field.originalColumnName;
                              let fieldIdx = $index
                            ) {
                              <tr
                                class="hover:bg-hover-default transition-colors"
                                [ngClass]="{ 'opacity-50': field.skip }"
                              >
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-secondary-text font-mono text-sm"
                                >
                                  {{ field.originalColumnName }}
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border align-top pt-3"
                                >
                                  <input
                                    nz-input
                                    [disabled]="field.skip"
                                    [ngModel]="field.suggestedFieldName"
                                    (ngModelChange)="
                                      updateFieldProperty(
                                        state.id,
                                        fieldIdx,
                                        'suggestedFieldName',
                                        $event
                                      )
                                    "
                                    class="bg-input-background text-primary-text text-sm font-mono transition-colors"
                                    [ngClass]="
                                      isValidField(field.suggestedFieldName)
                                        ? 'border-secondary-border focus:ring-focus-ring'
                                        : 'border-danger focus:ring-danger'
                                    "
                                  />
                                  @if (
                                    !isValidField(field.suggestedFieldName)
                                  ) {
                                    <span
                                      class="text-[10px] text-danger mt-1 block font-semibold"
                                      >{{ config.texts.invalidFieldName }}</span
                                    >
                                  }
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border align-top pt-3"
                                >
                                  <input
                                    nz-input
                                    [disabled]="field.skip"
                                    [ngModel]="field.suggestedTitle"
                                    (ngModelChange)="
                                      updateFieldProperty(
                                        state.id,
                                        fieldIdx,
                                        'suggestedTitle',
                                        $event
                                      )
                                    "
                                    class="bg-input-background border-secondary-border focus:ring-focus-ring text-primary-text text-sm"
                                  />
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border align-top pt-3"
                                >
                                  <nz-select
                                    [disabled]="field.skip"
                                    [ngModel]="
                                      getCombinedType(field.type, field.isArray)
                                    "
                                    (ngModelChange)="
                                      onDataTypeChange(
                                        state.id,
                                        fieldIdx,
                                        $event
                                      )
                                    "
                                    class="w-full"
                                  >
                                    @for (
                                      type of config.dataTypes;
                                      track type.value
                                    ) {
                                      <nz-option
                                        [nzValue]="type.value"
                                        [nzLabel]="type.label"
                                      ></nz-option>
                                    }
                                  </nz-select>
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-center align-top pt-3"
                                >
                                  <nz-switch
                                    [disabled]="field.skip"
                                    [ngModel]="field.required || false"
                                    (ngModelChange)="
                                      updateFieldProperty(
                                        state.id,
                                        fieldIdx,
                                        'required',
                                        $event
                                      )
                                    "
                                  ></nz-switch>
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-center align-top pt-3"
                                >
                                  <nz-switch
                                    [disabled]="field.skip"
                                    [ngModel]="field.sensitive || false"
                                    (ngModelChange)="
                                      updateFieldProperty(
                                        state.id,
                                        fieldIdx,
                                        'sensitive',
                                        $event
                                      )
                                    "
                                  ></nz-switch>
                                </td>
                                <td
                                  class="!bg-component-inner border-t border-secondary-border text-center align-top pt-3"
                                >
                                  <nz-switch
                                    [ngModel]="!field.skip"
                                    (ngModelChange)="
                                      updateFieldProperty(
                                        state.id,
                                        fieldIdx,
                                        'skip',
                                        !$event
                                      )
                                    "
                                  ></nz-switch>
                                </td>
                              </tr>

                              <tr [ngClass]="{ 'opacity-50': field.skip }">
                                <td
                                  class="!bg-component-inner border-b border-secondary-border"
                                ></td>
                                <td
                                  colspan="6"
                                  class="!bg-component-inner border-b border-secondary-border px-4 pt-2 pb-4"
                                >
                                  <div class="flex flex-col gap-1.5 w-full">
                                    <label
                                      class="text-xs font-semibold text-secondary-text uppercase tracking-wide"
                                      >{{ config.texts.colDesc }}
                                      <span class="text-danger">*</span></label
                                    >
                                    <textarea
                                      nz-input
                                      rows="2"
                                      required
                                      [disabled]="field.skip"
                                      [ngModel]="field.description"
                                      (ngModelChange)="
                                        updateFieldProperty(
                                          state.id,
                                          fieldIdx,
                                          'description',
                                          $event
                                        )
                                      "
                                      class="bg-input-background border-secondary-border focus:ring-focus-ring text-primary-text text-sm resize-y custom-scrollbar"
                                    ></textarea>
                                  </div>
                                </td>
                              </tr>
                            }
                          </tbody>
                        </nz-table>
                      </div>
                    </div>
                  }
                </div>
              }
            }
          </div>

          @if (hasFiles()) {
            <nz-upload
              nzType="drag"
              [nzMultiple]="true"
              [nzBeforeUpload]="beforeUpload"
              [nzShowUploadList]="false"
              class="w-full block bg-component-inner border-2 border-dashed border-primary-border rounded-xl hover:border-primary hover:bg-hover-default transition-all mt-4"
            >
              <p class="ant-upload-drag-icon text-center mt-8">
                <i
                  class="fad fa-cloud-upload-alt text-5xl text-primary opacity-80"
                ></i>
              </p>
              <p class="ant-upload-text text-lg font-bold text-heading mt-4">
                {{ config.texts.uploadTitle }}
              </p>
              <p
                class="ant-upload-hint text-secondary-text mb-8 max-w-md mx-auto"
              >
                {{ config.texts.supportedFiles }}
              </p>
            </nz-upload>
          }
        }

        <div
          class="w-full bg-alt-background border border-primary-border rounded-xl p-6 flex items-start gap-4"
        >
          <i class="fas fa-info-circle text-info text-2xl mt-1"></i>
          <div class="flex flex-col">
            <h3 class="text-lg font-bold text-heading">
              {{ config.texts.disclaimerTitle }}
            </h3>
            <p class="text-sm text-secondary-text mt-1 leading-relaxed">
              {{ config.texts.disclaimerText }}
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-4 border-t border-primary-border pt-6">
          <button
            (click)="goToDashboard()"
            class="bg-button-bg text-secondary-button-text border border-secondary-border hover:bg-hover-default font-semibold rounded-lg px-6 py-3 transition-colors"
          >
            {{ config.texts.cancelButton }}
          </button>
          <button
            (click)="startImport()"
            [disabled]="isImporting() || !isReadyToImport()"
            class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-8 py-3 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            @if (isImporting()) {
              <i class="fas fa-circle-notch fa-spin"></i>
            }
            <i class="fas fa-cloud-upload-alt"></i>
            {{ config.texts.importButton }}
          </button>
        </div>
      </div>
    }
  </div>
</div>
